import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  addChangeOrderForm,
  createChangeOrderFieldExtraProps,
} from "./addChangeOrderUtils";
// Import selectors & contexts similar to invoice implementation (placeholders for now)
// import { getJobsList } from "../../selectors/jobSelectors"; // adjust path as needed
import AppRoleContext from "context/AppRoleContext";
import {
  isProjectManager,
  isAssignee,
  isSupplierAdmin,
  isHomeOwner,
} from "core/utils/roleUtils";
import get from "lodash/get";
import startCase from "lodash/startCase";
import FormInputField from "components/TextFields/FormInputField"; // adjust path to actual component
import NewChangeOrderItem from "./NewChangeOrderItem";
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  FormControl,
  FormLabel,
} from "@mui/material";
import {
  getChangeOrderData,
  // getChangeOrderStatuses,
} from "selectors/changeOrderSelectors";
import {
  fetchChangeOrderByIdAction,
  createChangeOrderAction,
  // fetchChangeOrderStatusAction,
} from "actions/change_orderActions";
import { getHomeOwners } from "selectors/propertySelectors";
import { getJobsList } from "selectors/jobSelectors";
import { jobGetAll } from "actions/jobActions";
import { fetchHomeownersAction } from "actions/propertyActions";
import HiddenOn from "components/Wrappers/HiddenOn";
import { ModelDialog } from "components/Model";

const BUILDER_TYPE = "contractor";
const HOMEOWNER_TYPE = "Homeowner";

const AddChangeOrder = ({
  type,
  changeOrderId = null,
  selectedChangeOrder = null,
  referenceData = {},
  onClose,
  onSave,
  params = {},
  propertyId,
  projectId,
  projectData,
  jobId = null,
  builderSelectionType = null,
}) => {
  const appRoleDetails = useContext(AppRoleContext);
  const dispatch = useDispatch();

  const changeOrderData = useSelector(getChangeOrderData);

  const { homeOwnersList } = useSelector(getHomeOwners);
  const { jobsList } = useSelector(getJobsList);
  // const changeOrderStatuses = useSelector(getChangeOrderStatuses);

  const dynamicOptions = {
    clientName: homeOwnersList.map((homeOwner) => ({
      key: homeOwner.userId,
      value: homeOwner.userName,
      text: homeOwner.userName,
      data: homeOwner,
    })),
    jobId: jobsList.map((job) => ({
      key: job.jobId,
      value: job.jobName,
      text: job.jobName,
      data: job,
    })),
    // changeOrderStatusId: changeOrderStatuses.map((status) => {
    //   return {
    //     key: status.changeOrderStatusId,
    //     value: status.statusName,
    //     text: status.statusName,
    //     data: status,
    //   };
    // }),
  };

  // Support changeOrderId coming either directly as prop or nested in selectedChangeOrder
  const computedChangeOrderId =
    changeOrderId ||
    selectedChangeOrder?.data?.changeOrderId ||
    selectedChangeOrder?.changeOrderId;

  useEffect(() => {
    if (!computedChangeOrderId) return;
    // Only fetch if we don't already have this specific change order loaded
    if (changeOrderData?.changeOrderId !== computedChangeOrderId) {
      dispatch(fetchChangeOrderByIdAction(computedChangeOrderId));
    }
  }, [dispatch, computedChangeOrderId, changeOrderData?.changeOrderId]);

  const isSupplier =
    isSupplierAdmin(appRoleDetails) || isAssignee(projectData?.accessType);
  const isBuilder =
    isProjectManager(appRoleDetails, projectData?.projUserRole) &&
    !isAssignee(projectData?.accessType);
  const isHomeowner = isHomeOwner(appRoleDetails);

  // Placeholder lists (swap to real useSelector calls when endpoints ready)

  const [changeOrderItems, setChangeOrderItems] = useState([
    {
      id: Date.now(),
      itemName: "",
      unit: "",
      quantity: 0,
      unitPrice: 0.0,
      amount: 0.0,
    },
  ]);

  const { formFields, validationSchema } = addChangeOrderForm;

  useEffect(() => {
    const itemsList = changeOrderData?.changeOrderItemsList || [];
    setChangeOrderItems(
      itemsList.map((item) => ({
        ...item,
        id: item.changeOrderItemId || item.id,
      }))
    );
  }, [changeOrderData]);

  // Memoize initialValues so Formik doesn't think they changed on every render (which can cause reset loops)
  const initialValues = useMemo(() => {
    return addChangeOrderForm.defaultState(changeOrderData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify({ id: changeOrderData?.changeOrderId })]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: () => {
      // if (changeOrderItems.length === 0 || !changeOrderItems[0].itemName) {
      //   return;
      // }
      const totalChangeCost = parseFloat(
        changeOrderItems
          .reduce((acc, item) => acc + (item.amount || 0), 0)
          .toFixed(2)
      );
      const payload = {
        jobId,
        ...formik.values,
        propertyId,
        projectId,
        changeOrderItemsList: changeOrderItems,
        totalChangeCost,
      };
      dispatch(
        createChangeOrderAction({
          formData: payload,
          params: { projectId, propertyId },
          onSuccess: (response) => {
            onSave?.(response, payload);
            onClose?.(false);
          },
        })
      );
    },
  });

  const fetchRecieptentOptions = useCallback(() => {
    const queryData = {
      pageNumber: 1,
      pageSize: 500,
      projectId: projectData.projectId,
    };
    dispatch(jobGetAll(queryData));
    // dispatch(fetchChangeOrderStatusAction());
    if (isBuilder) {
      dispatch(fetchHomeownersAction({ pageSize: 500, pageNumber: 1 }));

      formik.setFieldValue("clientType", builderSelectionType);
    }
    if (isSupplier || isHomeowner || builderSelectionType === BUILDER_TYPE) {
      if (selectedChangeOrder && selectedChangeOrder.data?.changeOrderId) {
        return;
      } else {
        formik.setFieldValue(
          "clientName",
          get(projectData, "company.companyName", "")
        );
        const defaultClientEmail =
          get(projectData, "createdBy") ||
          get(projectData, "company.createdBy", "");
        formik.setFieldValue("clientEmail", defaultClientEmail);
        formik.setFieldValue(
          "clientMobile",
          get(projectData, "company.mobileNumber", "")
        );
        formik.setFieldValue(
          "clientCompanyId",
          get(projectData, "companyId", "")
        );

        formik.setFieldValue(
          "clientType",
          isSupplier || builderSelectionType === BUILDER_TYPE
            ? BUILDER_TYPE
            : HOMEOWNER_TYPE
        );
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isBuilder, isSupplier]);

  useEffect(() => {
    fetchRecieptentOptions();
  }, [fetchRecieptentOptions]);

  const updateDefaultFields = useCallback(() => {
    if (jobId && jobId > 0) {
      formik.setFieldValue("jobId", jobId);
    }
    if (projectData && projectData?.projectId) {
      if (
        builderSelectionType === BUILDER_TYPE ||
        (selectedChangeOrder && selectedChangeOrder.data?.changeOrderId)
      ) {
        return;
      } else {
        const assignedHomeowner = get(projectData, "property.userId", null);
        const getAssignedHomeownerDetails = homeOwnersList.find(
          (homeOwner) => homeOwner.userId === assignedHomeowner
        );
        if (getAssignedHomeownerDetails) {
          formik.setFieldValue(
            "clientName",
            getAssignedHomeownerDetails?.userName || ""
          );
          formik.setFieldValue(
            "clientEmail",
            getAssignedHomeownerDetails?.email || ""
          );
          formik.setFieldValue(
            "clientMobile",
            getAssignedHomeownerDetails?.mobileNumber || ""
          );
          formik.setFieldValue(
            "userId",
            getAssignedHomeownerDetails?.userId || ""
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeOwnersList, projectData]);

  useEffect(() => {
    updateDefaultFields();
  }, [updateDefaultFields]);

  const getHeaderName = () => {
    if (referenceData?.type === "job" && jobId) {
      return referenceData?.job?.jobName || "Job";
    }
    if (referenceData?.type === "project" && projectId) {
      return projectData?.projectName || "Project";
    }
    return true;
  };

  return (
    <div className="p-2 row create-change-order-container">
      <div className="change-order-form col-12 w-100">
        <HiddenOn isHidden={getHeaderName()}>
          Create Change Order for {getHeaderName()}
        </HiddenOn>
        <HiddenOn
          isHidden={
            !builderSelectionType || builderSelectionType === BUILDER_TYPE
          }
        >
          <Typography className="h3 mt-2 m-2">
            Create change order on behalf of {builderSelectionType}
          </Typography>
        </HiddenOn>
        <div className="stepContent container-fluid row">
          {formFields.map((field) => {
            const extraInputProps = get(field, "extraInputProps", {});
            let extraProps =
              typeof extraInputProps === "function"
                ? extraInputProps(formik)
                : extraInputProps;

            if (createChangeOrderFieldExtraProps[field.key]) {
              extraProps = {
                ...extraProps,
                ...createChangeOrderFieldExtraProps[field.key][
                  isSupplier || builderSelectionType === BUILDER_TYPE
                    ? "supplier"
                    : "builder"
                ](formik),
              };
            }
            if (field.key === "jobId" && jobId) {
              return null;
            }
            return (
              <FormInputField
                key={field.key}
                id={field.id}
                field={{
                  ...field,
                  options: dynamicOptions[field.key] || field.options,
                }}
                formik={formik}
                value={formik.values[field.key]}
                dynamicOptions={dynamicOptions}
                {...extraProps}
              />
            );
          })}
        </div>
        <div>
          <NewChangeOrderItem
            setItems={setChangeOrderItems}
            items={changeOrderItems}
          />
        </div>
        <div>
          <Typography variant="body1" className="mt-1 ">
            <sup style={{ color: "red", fontSize: "1em", top: 0 }}>*</sup>
            <strong>Required Fields</strong>
          </Typography>
        </div>
        <div>
          {formik.errors && Object.keys(formik.errors).length > 0 && (
            <div className="error-message">
              <span style={{ fontSize: "12px", color: "red" }}>
                Please fill all the required fields{" "}
                {Object.keys(formik.errors)
                  .map((key) => startCase(key))
                  .join(", ")}
              </span>
            </div>
          )}
        </div>
        <div className="mt-4 d-flex justify-content-center">
          <button className="secondaryButton" type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="primaryButton"
            type="button"
            onClick={formik.handleSubmit}
          >
            {changeOrderData ? "Update Change Order" : "Create Change Order"}
          </button>
        </div>
      </div>
    </div>
  );
};

const withTypeSelection = (Component) => {
  function WithTypeSelection(props) {
    const { selectedChangeOrder } = props;
    const appRoleDetails = useContext(AppRoleContext);
    const isPM = isProjectManager(appRoleDetails);

    const [showDialog, setShowDialog] = useState(isPM); // open dialog if PM
    const [builderSelectionType, setBuilderSelectionType] = useState(
      isPM ? HOMEOWNER_TYPE : null
    ); // default to Homeowner if PM

    const getBuilderTypeSelectionType = () => {
      if (
        selectedChangeOrder &&
        selectedChangeOrder.data?.changeOrderId &&
        isPM &&
        get(selectedChangeOrder, "data.clientType", null) === BUILDER_TYPE
      ) {
        return BUILDER_TYPE;
      }
      return builderSelectionType;
    };
    // Show modal only if user is PM and hasn't confirmed yet
    if (
      !(selectedChangeOrder && selectedChangeOrder.data?.changeOrderId) &&
      showDialog
    ) {
      return (
        <ModelDialog
          open={true}
          handleClose={
            () => {} /* optionally disable closing without confirmation */
          }
          dialogTitle=""
          dialogContent={
            <>
              <FormControl>
                <FormLabel id="demo-radio-buttons-group-label">
                  <Typography className="h4">
                    Create Change Order on Behalf of
                  </Typography>
                </FormLabel>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  defaultValue="Homeowner"
                  name="radio-buttons-group"
                  onChange={(e) => {
                    setBuilderSelectionType(e.target.value);
                  }}
                >
                  <FormControlLabel
                    value={HOMEOWNER_TYPE}
                    control={<Radio />}
                    label={HOMEOWNER_TYPE}
                  />
                  <FormControlLabel
                    value={BUILDER_TYPE}
                    control={<Radio />}
                    label="Supplier"
                  />
                </RadioGroup>
              </FormControl>
              <div className="mt-4 d-flex justify-content-center">
                <button
                  className="secondaryButton me-2"
                  onClick={() => {
                    setShowDialog(false);
                    if (props?.onClose) props.onClose();
                  }}
                >
                  Cancel
                </button>
                <button
                  className="primaryButton ms-1 col-5"
                  style={{ width: "8rem" }}
                  onClick={() => setShowDialog(false)}
                >
                  Confirm
                </button>
              </div>
            </>
          }
        />
      );
    }
    return (
      <Component
        {...props}
        builderSelectionType={getBuilderTypeSelectionType()}
      />
    );
  }
  return WithTypeSelection;
};
export const WithTypeSelection = withTypeSelection(AddChangeOrder);

export default AddChangeOrder;
